import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { notifyIcon, number, quote } from "../../../config";
import DetailedFlowElementDetails from "../../../shared/components/FlowElement/DetailedFlowElementDetails";
import { getNotification, isNonNegativeOrNull } from "../../../utils/common";
import { GridContext } from "../../../utils/kendo";
import { checkFlowInsertionFromFlows } from "../../TaskSidebar/sidebar.helper";
import { WorkflowContext } from "../WorkflowContextProvider";
import { addElementAtBottomForFlowElements, remove, update } from "../workflowHelper";
import { FlowElementsContext } from "./FlowElementsContextProvider";

/**
 * Parent Component : FLowElementDetails
 * This defines the logic particular to workflow used for DetailedFlowElements and FlowElementCommandCell
 * @author Sarthak Arora
 */
const WorkflowDetailedFlowElementDetails = (props) => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { isNewTask, flowElements } = useSelector((state) => state.taskSidebar);
  const { workflowDefaults, returnToOriginal } = useContext(WorkflowContext);
  const taskAssignmentTypes = workflowDefaults?.taskAssignmentTypes;
  const workflowAssignmentTypes = workflowDefaults?.workflowAssignmentTypes;
  const { projectId, workflowId } = useParams();

  const { flowElementData, setFlowElementData, existingFlowElements, entity, addFlowElement, editFlowElement, deleteFlowElement, taskElement } =
    useContext(GridContext);

  const {
    flowAssignmentType,
    flowWorkflowAssignmentType,
    flowTaskAssignmentId,
    flowWorkflowAssignmentId,
    flowElementDueDate,
    flowElementDelay,
    isApproval,
    isFlowLocked,
  } = useContext(FlowElementsContext);


  const projectWorkflowOrUser = flowTaskAssignmentId?.AssignmentType === number.THREE 
    ? (flowWorkflowAssignmentId === null || (flowWorkflowAssignmentType && flowWorkflowAssignmentId)) 
    : true;

  const requiredFields = (isDynamicFlow) => {
    if (isDynamicFlow) {
      return isNonNegativeOrNull(flowElementDueDate) && isNonNegativeOrNull(flowElementDelay);
    } else {
      return (
        flowAssignmentType &&
        flowTaskAssignmentId &&
        projectWorkflowOrUser &&
        isNonNegativeOrNull(flowElementDueDate) &&
        isNonNegativeOrNull(flowElementDelay)
      );
    }
  };

  const assignmentProjectIdCondition =
    flowTaskAssignmentId?.AssignmentType === number.THREE
      ? flowTaskAssignmentId?.value
      : defaultDetails?.allProjectsList?.find((item) => item.IsPersonal === number.ONE && item.CreatedBy === flowTaskAssignmentId?.value)?.ProjectId;

  const createPayload = (isNewElement, dataItem) => {
    let payload = dataItem.dynamicFlow
      ? {
          variableId: dataItem?.selectedActiveMember?._id,
          flowElementId: dataItem?.id,
          flowElementType: 2,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: isNewElement ? flowElementData?.length : dataItem?.sortId,
          isApproval,
          isFlowLocked,
          assignBasedOnCreator: dataItem?.profileType === "ticketCreator" ? 1 : 0,
        }
      : {
          flowElementId: dataItem?.id,
          flowElementType: 1,
          taskAssignmentType: flowTaskAssignmentId?.AssignmentType,
          taskAssignmentId: flowTaskAssignmentId?.value,
          workflowAssignmentType: flowWorkflowAssignmentType?.value,
          workflowAssignmentId: flowWorkflowAssignmentId?.value,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: isNewElement ? flowElementData?.length : dataItem?.sortId,
          assignmentProjectId: assignmentProjectIdCondition,
          isApproval,
          isFlowLocked,
        };
    return payload;
  };

  /**handles edit in case of existing flow element*/
  const handleFlowElementEdit = async (dataItem) => {
    let payload = createPayload(false, dataItem);
    let isNotValidInsertion, delgationBasedInsertion;
    payload["workflowId"] = entity;
    if (dataItem.dynamicFlow) {
      update({ ...payload, id: dataItem?.id }, existingFlowElements, setFlowElementData, requiredFields(true));
      entity && (await editFlowElement(payload));
      return;
    }
    const itemIndex = existingFlowElements?.findIndex((flow) => flow.id === dataItem?.id);
    isNotValidInsertion = checkFlowInsertionFromFlows(existingFlowElements, itemIndex - number.ONE, itemIndex + number.ONE, payload, false);
    delgationBasedInsertion = !!(
      returnToOriginal &&
      payload.assignmentProjectId === projectId &&
      payload.workflowAssignmentType === number.FIVE &&
      payload.workflowAssignmentId === workflowId
    );
    if (isNotValidInsertion || delgationBasedInsertion) getNotification(quote.CAN_NOT_COEXIST, notifyIcon?.WARNING_ICON);
    if (!isNotValidInsertion && !delgationBasedInsertion) {
      update({ ...payload, id: dataItem?.id }, existingFlowElements, setFlowElementData, requiredFields(false));
      entity && (await editFlowElement(payload));
    }
  };

  /**handles addition of new flow element*/

  const handleNewFlowElement = async (dataItem) => {
    const newDataItem = createPayload(true, dataItem);
    let payload = {
      workflowElements: [newDataItem],
      isNewTask,
    };
    payload["workflowId"] = entity;
    const insertionIndex = flowElementData?.length - number.ONE;
    const response =
      entity &&
      requiredFields(dataItem.dynamicFlow) &&
      !checkFlowInsertionFromFlows(
        taskElement ? flowElements : flowElementData,
        insertionIndex - number.ONE,
        insertionIndex,
        newDataItem,
        taskElement ? false : true
      ) &&
      !(
        newDataItem.assignmentProjectId === projectId &&
        newDataItem.workflowAssignmentType === number.FIVE &&
        newDataItem.workflowAssignmentId === workflowId
      ) &&
      (await addFlowElement(payload));
    const id = entity ? response?.flowId : null;
    const addFlowPayload = {
      newDataItem,
      existingFlowElements,
      setFlowElementData,
      id,
      entity,
      requiredFields: requiredFields(dataItem.dynamicFlow),
    };
    addElementAtBottomForFlowElements(addFlowPayload, projectId, workflowId);
  };

  /**handles deletion of existing flow element*/
  const handleDeleteFlowElement = async (dataItem) => {
    let payload = {
      flowElementId: dataItem?.id,
    };
    payload["workflowId"] = entity;
    remove(dataItem, existingFlowElements, setFlowElementData);
    getNotification(quote.FLOW_ELEMENT_DELETED, notifyIcon.SUCCESS_ICON);
    entity && (await deleteFlowElement(payload));
  };

  return (
    <div>
      <DetailedFlowElementDetails
        {...props}
        taskAssignmentTypes={taskAssignmentTypes}
        workflowAssignmentTypes={workflowAssignmentTypes}
        handleFlowElementEdit={handleFlowElementEdit}
        handleNewFlowElement={handleNewFlowElement}
        handleDeleteFlowElement={handleDeleteFlowElement}
      />
    </div>
  );
};

export default WorkflowDetailedFlowElementDetails;
